import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Users, ArrowRight, Clock, Mail, FileText, FileWarning, Inbox } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { friendlyError } from "@/lib/errors";

const RELATIONSHIP_LABEL = {
  spouse: "Spouse",
  child: "Child",
  parent: "Parent",
  sibling: "Sibling",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function AttorneyDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attorney/cases");
      setRows(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load your assigned cases."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10" data-testid="attorney-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="pill pill-ai mb-3 inline-flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5" /> Attorney workspace
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold" style={{ color: "var(--text-primary)" }}>
              Assigned cases
            </h1>
            <p className="mt-2 text-sm sm:text-[15px]" style={{ color: "var(--text-secondary)" }}>
              Read-only view of every family-based petition your clients have shared with you.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/inbox"
              className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border font-semibold"
              style={{borderColor:"var(--border)",color:"var(--text-primary)",background:"var(--bg-card)"}}
              data-testid="attorney-inbox-link"
            >
              <Inbox className="w-4 h-4" /> Bulk Inbox
            </Link>
            <Link
              to="/attorney/review"
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="attorney-review-link"
            >
              <FileWarning className="w-4 h-4" /> Review all changes
            </Link>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" style={{ color: "var(--accent-copper)" }} />
                <span data-testid="attorney-case-count">{rows.length} case{rows.length === 1 ? "" : "s"}</span>
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="attorney-loading">
            Loading assigned cases…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div
            className="card-surface p-10 text-center"
            data-testid="attorney-empty-state"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                 style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}>
              <Briefcase className="w-6 h-6" />
            </div>
            <h2 className="font-display text-xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>
              No cases assigned yet
            </h2>
            <p className="text-sm max-w-md mx-auto mb-4" style={{ color: "var(--text-secondary)" }}>
              When a client assigns you to their case using your email, it will appear here automatically.
              You'll have read-only visibility into their timeline, documents, and USCIS notices.
            </p>
            <a
              href="/attorney-handout"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-sm inline-flex items-center gap-1.5"
              style={{ border: "1px solid var(--border-stone)" }}
              data-testid="attorney-handout-link"
            >
              <FileText className="w-4 h-4" /> View the family-based checklist
            </a>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="attorney-case-grid">
            {rows.map((r) => (
              <Link
                key={r.assignment_id}
                to={`/attorney/case/${r.case_id}`}
                className="card-surface p-5 hover:shadow-lg transition-shadow block"
                data-testid={`attorney-case-card-${r.case_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="pill pill-ai text-[10px] uppercase tracking-widest">
                      {RELATIONSHIP_LABEL[r.relationship] || r.relationship || "DS-260"}
                    </div>
                    <div className="mt-2 text-lg font-bold font-display" style={{ color: "var(--text-primary)" }}>
                      {r.beneficiary_name || "Unnamed beneficiary"}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                      Petitioner: <span className="font-semibold">{r.petitioner_name || "—"}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 shrink-0 mt-1" style={{ color: "var(--accent-copper)" }} />
                </div>
                <div className="border-t pt-3 mt-3 space-y-1.5" style={{ borderColor: "var(--border-stone)" }}>
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
                    <Users className="w-3.5 h-3.5" />
                    <span>Client: <strong style={{ color: "var(--text-primary)" }}>{r.client_name || r.client_email}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
                    <Mail className="w-3.5 h-3.5" />
                    <span className="truncate">{r.client_email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
                    <Clock className="w-3.5 h-3.5" />
                    <span>Assigned {formatDate(r.assigned_at)}</span>
                  </div>
                  {r.note && (
                    <div className="text-xs mt-2 italic px-3 py-2 rounded"
                         style={{ background: "var(--bg-cream)", color: "var(--text-secondary)" }}>
                      "{r.note}"
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
